import React, { useState, useContext } from 'react';
import Downshift from 'downshift';
import PropTypes from 'prop-types';
import qs from 'qs';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

import getResponseJson from '../../../services/getresponsejson';
import codeCategoryTypes from '../../../config/codecategoryconstants';
import { RouteFetcherContext } from '@mk-redbox/route-fetcher';

const styles = theme => ({
  root: {
    position:'relative'
  },
  primaryContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    border: '1px solid black',
    padding:'.8em'
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    '& label': {
      marginRight:'8px'
    }
  },
  selectedItemContainer: {
    display: 'inline-block',
    marginLeft: '1em'
  },
  optionsContainer: {
    position: ['fixed', '!important'],
    marginLeft: '62px',
    width: '11em',
    height: '170px',
    overflowY: 'auto',
    border: '1px solid black',
    backgroundColor:'white',
    marginTop:'-19px',
    cursor:'pointer',
    padding:'4px',
    zIndex: '9999',
    '& div': {
      borderBottom:'1px solid black'
    }
  }
});

// for server api call param
const API_PARAM_ENCODING_TYPE = {
  DX: 1,
  PROCEDURE: 2
};

// for server api call param
const API_PARAM_ICD_VERSION = {
  ICD10: 10,
  PROCEDURE: 3
};

const getResults = (
  term,
  encodingType,
  dos,
  icdVersion,
  category,
  backendRoutes
) => {
  const { CmsCodeAutoCompleteWithHccCodes } = backendRoutes;

  const params = { term, encodingType, dos, icdVersion, category };

  const queryString = qs.stringify(params);

  const url = `${CmsCodeAutoCompleteWithHccCodes}?${queryString}`;

  return fetch(url, {
    credentials: 'include'
  })
  .then(getResponseJson)
  .catch(e => {
    console.error(e.toString());
  });
};

const DxAutocomplete = ({
  dateOfService,
  selectedEncoding,
  setSelectedEncoding,
  classes,
  category = codeCategoryTypes.HCC
}) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const backendRoutes = useContext(RouteFetcherContext);

  const handleInputChange = e => {
    const value = e.target.value;

    if (!value || value.length < 3) {
      setOptions([]);
      return;
    }

    setIsLoading(true);

    getResults(
      value,
      API_PARAM_ENCODING_TYPE.DX,
      dateOfService,
      API_PARAM_ICD_VERSION.ICD10,
      category,
      backendRoutes
    )
    .then(responseJson => {
      const { result } = responseJson;
      setOptions(result);
      setIsLoading(false);
    })
    .catch(e => {
      console.error(e.toString());
      setIsLoading(false);
    });
  };

  const handleReset = () => {
    setSelectedEncoding(null);
    setOptions([]);
    setIsLoading(false);
    setInputValue('');
  }

  return (
    <Downshift
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      selectedItem={selectedEncoding}
      onChange={setSelectedEncoding}
      itemToString={item => (item ? item.code : '')}
    >
      {({
        getInputProps,
        getLabelProps,
        isOpen,
        getItemProps,
        highlightedIndex,
        selectedItem
      }) => {
        return (
          <div className={classes.root}>
            <div className={classes.primaryContainer}>

            {/* input section */}
            <div className={classes.inputContainer}>
              <label {...getLabelProps()}>Encoding</label>
              <input
                {...getInputProps({
                  placeholder: 'Search Encodings',
                  onChange: handleInputChange
                })}
              />
              {
                isLoading
                  ? (<CircularProgress variant="indeterminate" size="20px" />)
                  : null
              }
            </div>

            {/* selected item section */}
            <div className={classes.selectedItemContainer}>
              <span>
                Selected Encoding: {
                  selectedEncoding === null ?
                    'NONE' :
                    selectedEncoding.code
                }
              </span>

              <button
                onClick={handleReset}
                disabled={selectedEncoding === null}
              >
                <span>CLEAR</span>
              </button>
            </div>

            </div>

            {/* items list section */}
            {
              isOpen && options.length === 1 && options[0].code === null ? (
                <div className={classes.optionsContainer}>
                  <div
                    {...getItemProps({
                        key: null,
                        item: null,
                        index: 0,
                        style: {
                        backgroundColor: 'lightgray',
                        fontWeight: 'bold'
                      }
                    })}
                  >
                    NO MATCHES FOUND
                  </div>
                </div>
              ) : isOpen && options.length > 0 ? (
                <div className={classes.optionsContainer}>
                  {
                    options.map((o, i) => (
                      <div
                        {...getItemProps({
                          key: o.cmsCodeId,
                          item: o,
                          index: i,
                          style: {
                            backgroundColor: highlightedIndex === i ?
                              'lightgray' :
                              'white',
                            fontWeight: selectedItem &&
                              selectedItem.cmsCodeId === o.cmsCodeId ?
                                'bold' :
                                'normal'
                          }
                        })}
                      >
                        {o.display}
                      </div>
                    ))
                  }
                </div>
              ) : null
            }
          </div>
        );
      }}
    </Downshift>
  );
};

DxAutocomplete.propTypes = {
  dateOfService: PropTypes.string.isRequired,
  category: PropTypes.number
};

export default withStyles(styles, { withTheme: true })(DxAutocomplete);
